import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { CompanyDirectoryService } from '@shared/services/company-directory.service';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import {
  ATTENDANCE_ALLOWED_SOURCE_OPTIONS,
  ATTENDANCE_SOURCE_OVERRIDE_OPTIONS,
  ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS,
  ATTENDANCE_TIMEZONE_OPTIONS,
  AttendanceConfigFormValue,
  AttendanceConfigListResponse,
  AttendanceConfigRecord,
  AttendanceSourceRuleFormValue,
  AttendanceZoneListResponse,
  AttendanceZoneOption,
  buildAttendanceConfigPayload,
  createEmptyAttendanceConfigForm,
  createEmptyAttendanceSourceRuleForm,
  findAttendanceConfigForScope,
  extractAttendanceZones,
  formatAttendanceTrackingInterval,
  getAttendanceSourceLabelKey,
  mapAttendanceConfigToForm,
  usesDeviceRules,
  usesDuplicateWindow,
  usesGeo,
  usesMistypedNormalization,
  usesTimezone,
  usesUntypedNormalization,
  validateAttendanceConfigForm,
} from '@shared/utils/attendance-config.util';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { firstValueFrom } from 'rxjs';
import { DeviceRecord, DevicesResponse } from '../../devices/device.interface';
import { DailyScheduleConfig } from '../../attendances/daily-schedule/daily-schedule.interface';
import { WorkCycleConfig } from '../../attendances/work-cycle/work-cycle.interface';
import { RestConfigurationConfig } from '../../attendances/rest-configuration/rest-configuration.interface';

interface CompanyRecord {
  companiesRecordId: string;
  center: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface CompaniesResponse {
  data?: CompanyRecord[];
  items?: CompanyRecord[];
  results?: CompanyRecord[];
  companiesRecords?: CompanyRecord[];
}

interface OrgUnitResponseItem {
  orgUnitId: string;
  center: string;
  companiesRecordId: string;
  code: string;
  name: string;
  type: string;
  parentId?: string | null;
  level?: number | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitResponseItem[];
  items?: OrgUnitResponseItem[];
  results?: OrgUnitResponseItem[];
  orgUnits?: OrgUnitResponseItem[];
}

interface OrgUnitNode extends OrgUnitResponseItem {
  level: number;
  isActive: boolean;
  children: OrgUnitNode[];
}

interface LevelSummary {
  level: number;
  count: number;
}

interface OrgUnitForm {
  companiesRecordId: string;
  code: string;
  name: string;
  type: string;
  parentId: string | null;
}

interface WorkCyclesResponse {
  data?: WorkCycleConfig[];
  items?: WorkCycleConfig[];
  results?: WorkCycleConfig[];
  workCycles?: WorkCycleConfig[];
}

interface OrgUnitWorkAssignment {
  workAssignmentId?: string;
  center?: string;
  companiesRecordId: string;
  orgUnitId?: string | null;
  orgPath?: string | null;
  employeeId?: string | null;
  workCycleId: string | null;
  restConfigurationId: string | null;
  cycleStartDate: string | null;
  cycleOffset: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  toleranceRules?: WorkAssignmentToleranceRules | null;
  overtimePolicy?: WorkAssignmentOvertimePolicy | null;
  turnPolicy?: WorkAssignmentTurnPolicy | null;
  version?: number;
  updatedBy?: string;
  updatedAt?: string;
  isActive?: boolean;
}

interface WorkAssignmentToleranceRules {
  entryToleranceMinutes?: number | null;
  earlyExitToleranceMinutes?: number | null;
  roundingMarginMinutes?: number | null;
}

interface WorkAssignmentOvertimePolicy {
  ignoreOvertime?: boolean | null;
}

interface WorkAssignmentTurnPolicy {
  enableAutoClose?: boolean | null;
  autoCloseGraceMinutes?: number | null;
}

interface OrgUnitLaborConfigResponse {
  entity: OrgUnitResponseItem & {
    path?: string | null;
    code?: string | null;
    metadata?: Record<string, unknown> | null;
  };
  workAssignment?: OrgUnitWorkAssignment | null;
  attendanceConfig?: unknown | null;
  devices?: {
    deviceId: string;
    serial?: string;
    name?: string;
    companiesRecordId?: string;
    isActive?: boolean;
  }[];
}

interface EffectiveOperationalConfigResponse {
  workAssignment: {
    configFrom: 'employee' | 'orgUnit' | 'companiesRecord' | 'defaults';
    configFromId?: string | null;
    value: OrgUnitWorkAssignment | null;
  };
  attendanceConfig: {
    configFrom: 'employee' | 'orgUnit' | 'companiesRecord' | 'center' | 'defaults';
    configFromId?: string | null;
    attendanceConfigId?: string | null;
    value: Omit<AttendanceConfigRecord, 'attendanceConfigId'> & { attendanceConfigId?: string };
  };
}

interface WorkAssignmentForm {
  enabled: boolean;
  workCycleId: string;
  restConfigurationId: string;
  cycleStartDate: string;
  effectiveFrom: string;
  effectiveTo: string;
  toleranceRules: {
    entryToleranceMinutes: number | '';
    earlyExitToleranceMinutes: number | '';
    roundingMarginMinutes: number | '';
  };
  overtimePolicy: {
    ignoreOvertime: boolean;
  };
  turnPolicy: {
    enableAutoClose: boolean;
    autoCloseGraceMinutes: number | '';
  };
}

interface AssignmentCalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isStart: boolean;
  isEnd: boolean;
  isInRange: boolean;
  isToday: boolean;
}

interface AssignmentScheduleCalendarDay extends AssignmentCalendarDay {
  previewDay: WorkCyclePreviewDay | null;
  weekdayLabel: string;
}

interface AssignmentScheduleCalendarMonth {
  label: string;
  days: AssignmentScheduleCalendarDay[];
}

interface WorkCyclePreviewDay {
  dayOrder: number;
  dayType: 'work' | 'rest';
  scheduleName: string;
  scheduleRange: string;
  expectedHours: string;
  isRest: boolean;
}

interface OrgUnitUser {
  membershipId: string;
  uid: string;
  center: string;
  centers: string[];
  userKind: string;
  scopeMode: string;
  email: string;
  displayName: string;
  photoUrl: string | null;
  status: string;
  roles: string[];
  hasExplicitAllow?: boolean;
  hasExplicitDeny?: boolean;
}

interface OrgUnitUsersResponse {
  allow: OrgUnitUser[];
  deny: OrgUnitUser[];
  summary?: {
    allowCount: number;
    denyCount: number;
  };
}

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

type DetailMode = 'view' | 'create' | 'edit';
type DetailTab = 'general' | 'configuration' | 'devices' | 'attendanceConfiguration' | 'users';
type WorkspaceView = 'tree' | 'detail';

@Component({
  selector: 'app-org-units',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    DateTimePickerFieldComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    TranslateModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    LoadingStateComponent,
  ],
  templateUrl: './org-units.component.html',
  styleUrl: './org-units.component.scss',
})
export class OrgUnitsComponent implements OnInit {
  private static readonly FILTER_STATE_KEY = 'org-units:filters';
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;
  companies: CompanyRecord[] = [];
  allOrgUnits: OrgUnitResponseItem[] = [];
  companyOrgUnits: OrgUnitNode[] = [];
  levelSummaries: LevelSummary[] = [];

  selectedCompanyId = '';
  searchTerm = '';
  companySearchTerm: string | CompanyRecord = '';
  showAllCompanyOptions = false;
  selectedOrgUnitId = '';
  workspaceView: WorkspaceView = 'tree';

  detailMode: DetailMode = 'view';
  activeDetailTab: DetailTab = 'general';
  isSaving = false;
  isDeleting = false;
  isMoving = false;
  isLoadingUsers = false;
  isSavingUsers = false;
  form: OrgUnitForm = this.createEmptyForm();
  orgUnitTypeOptions: string[] = [];
  isLoadingTypeOptions = false;
  draggedOrgUnitId = '';
  dragOverOrgUnitId = '';
  isRootDropActive = false;
  collapsedNodeIds = new Set<string>();
  workCycles: WorkCycleConfig[] = [];
  selectedWorkCycleDetail: WorkCycleConfig | null = null;
  dailySchedules: DailyScheduleConfig[] = [];
  restConfigurations: RestConfigurationConfig[] = [];
  devices: DeviceRecord[] = [];
  selectedDeviceIds: string[] = [];
  zoneOptions: AttendanceZoneOption[] = [];
  selectedOrgUnitDetail: OrgUnitLaborConfigResponse['entity'] | null = null;
  selectedWorkAssignment: OrgUnitWorkAssignment | null = null;
  workAssignmentForm: WorkAssignmentForm = this.createEmptyWorkAssignmentForm();
  assignmentScheduleMonthIndex = 0;
  selectedAttendanceConfig: AttendanceConfigRecord | null = null;
  attendanceConfigForm: AttendanceConfigFormValue = createEmptyAttendanceConfigForm();
  effectiveOperationalConfig: EffectiveOperationalConfigResponse | null = null;
  private effectiveOperationalConfigRequestId = 0;
  allowUsers: OrgUnitUser[] = [];
  denyUsers: OrgUnitUser[] = [];
  availableUsers: OrgUnitUser[] = [];
  selectedAllowUserId = '';
  selectedDenyUserId = '';

  isLoadingCompanies = true;
  isLoadingOrgUnits = true;
  isLoadingDetail = false;
  isLoadingWorkCycles = false;
  readonly attendanceSourceOptions = [...ATTENDANCE_ALLOWED_SOURCE_OPTIONS];
  readonly attendanceSourceRuleOptions = [...ATTENDANCE_SOURCE_OVERRIDE_OPTIONS];
  readonly attendanceTrackingIntervalOptions = [...ATTENDANCE_TRACKING_INTERVAL_PRESET_SECONDS];
  readonly attendanceTimezoneOptions = [...ATTENDANCE_TIMEZONE_OPTIONS];
  readonly getAttendanceSourceLabelKey = getAttendanceSourceLabelKey;
  readonly formatAttendanceTrackingInterval = formatAttendanceTrackingInterval;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly companySelection = inject(CompanySelectionService);
  private readonly companyDirectory = inject(CompanyDirectoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly workCyclesCache = new Map<string, CacheEntry<WorkCycleConfig[]>>();
  private readonly dailySchedulesCache = new Map<string, CacheEntry<DailyScheduleConfig[]>>();
  private readonly restConfigurationsCache = new Map<string, CacheEntry<RestConfigurationConfig[]>>();
  private readonly devicesCache = new Map<string, CacheEntry<DeviceRecord[]>>();
  private readonly zonesCache = new Map<string, CacheEntry<AttendanceZoneOption[]>>();
  private readonly detailCache = new Map<string, CacheEntry<OrgUnitLaborConfigResponse>>();
  private readonly attendanceConfigCache = new Map<string, CacheEntry<AttendanceConfigRecord | null>>();
  private readonly usersCache = new Map<string, CacheEntry<{ allow: OrgUnitUser[]; deny: OrgUnitUser[]; available: OrgUnitUser[] }>>();
  private typeOptionsDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private typeOptionsRequestId = 0;

  async ngOnInit(): Promise<void> {
    this.destroyRef.onDestroy(() => {
      if (this.typeOptionsDebounceHandle) {
        clearTimeout(this.typeOptionsDebounceHandle);
      }
    });
    this.restoreFilterState();
    await this.loadCompanies();
    await this.loadOrgUnits();
  }

  get selectedCompany(): CompanyRecord | undefined {
    return this.companies.find((company) => company.companiesRecordId === this.selectedCompanyId);
  }

  get filteredCompanies(): CompanyRecord[] {
    if (this.showAllCompanyOptions) {
      return this.companies;
    }

    const normalizedTerm = this.getCompanySearchText(this.companySearchTerm).trim().toLowerCase();
    if (!normalizedTerm) {
      return this.companies;
    }

    return this.companies.filter((company) =>
      [company.name, company.code, company.center]
        .filter((value): value is string => !!value)
        .some((value) => value.toLowerCase().includes(normalizedTerm))
    );
  }

  get selectedOrgUnit(): OrgUnitNode | undefined {
    return this.selectedOrgUnitId ? this.findNodeById(this.companyOrgUnits, this.selectedOrgUnitId) : undefined;
  }

  get totalUnits(): number {
    return this.flattenNodes(this.companyOrgUnits).length;
  }

  get activeUnits(): number {
    return this.flattenNodes(this.companyOrgUnits).filter((unit) => unit.isActive).length;
  }

  get totalLevels(): number {
    const units = this.flattenNodes(this.companyOrgUnits);
    return units.length
      ? units.reduce((maxLevel, unit) => Math.max(maxLevel, unit.level), 0) + 1
      : 0;
  }

  get filteredRoots(): OrgUnitNode[] {
    const normalizedTerm = this.searchTerm.trim().toLowerCase();
    return normalizedTerm ? this.filterTree(this.companyOrgUnits, normalizedTerm) : this.companyOrgUnits;
  }

  get hasSelection(): boolean {
    return Boolean(this.selectedCompanyId);
  }

  get isViewMode(): boolean {
    return this.detailMode === 'view';
  }

  get isCreateMode(): boolean {
    return this.detailMode === 'create';
  }

  get isEditMode(): boolean {
    return this.detailMode === 'edit';
  }

  get isGeneralFormValid(): boolean {
    return Boolean(
      this.form.companiesRecordId.trim() &&
      this.form.name.trim() &&
      this.form.type.trim()
    );
  }

  get canCreate(): boolean {
    return this.hasAccess('create');
  }

  get canRead(): boolean {
    return this.hasAccess('read');
  }

  get canUpdate(): boolean {
    return this.hasAccess('update');
  }

  get canDelete(): boolean {
    return this.hasAccess('delete');
  }

  get canReadAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'read' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'access' })
    );
  }

  get canManageAttendanceConfig(): boolean {
    return (
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'create' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'update' }) ||
      this.authorizationService.hasAccess({ resource: 'attendanceConfigs', action: 'delete' })
    );
  }

  get parentOptions(): OrgUnitNode[] {
    const flattened = this.flattenNodes(this.companyOrgUnits);
    if (!this.isEditMode || !this.selectedOrgUnit) {
      return flattened;
    }

    const excludedIds = new Set(this.flattenNodes([this.selectedOrgUnit]).map((unit) => unit.orgUnitId));
    return flattened.filter((unit) => !excludedIds.has(unit.orgUnitId));
  }

  get selectedOrgUnitParentName(): string {
    const parentId = this.selectedOrgUnitDetail?.parentId || this.selectedOrgUnit?.parentId;
    if (!parentId) {
      return this.t('orgUnitsCatalog.root');
    }

    return this.findNodeById(this.companyOrgUnits, parentId)?.name || this.t('orgUnitsCatalog.root');
  }

  get selectedOrgUnitChildrenCount(): number {
    return this.selectedOrgUnit?.children.length || 0;
  }

  get hasDraggedNode(): boolean {
    return Boolean(this.draggedOrgUnitId);
  }

  get availableAllowUsers(): OrgUnitUser[] {
    const deniedIds = new Set(this.denyUsers.map((user) => user.uid));
    return this.availableUsers.filter((user) => !user.hasExplicitAllow && !deniedIds.has(user.uid));
  }

  get availableDenyUsers(): OrgUnitUser[] {
    const allowedIds = new Set(this.allowUsers.map((user) => user.uid));
    return this.availableUsers.filter((user) => !user.hasExplicitDeny && !allowedIds.has(user.uid));
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('orgUnitsCatalog.detail.createTitle');
    }

    if (this.isEditMode) {
      return this.t('orgUnitsCatalog.detail.editTitle');
    }

    return this.t('orgUnitsCatalog.detail.title');
  }

  get detailSubtitle(): string {
    if (this.isCreateMode) {
      return this.t('orgUnitsCatalog.detail.createDescription');
    }

    if (this.isEditMode) {
      return this.t('orgUnitsCatalog.detail.editDescription');
    }

    return this.t('orgUnitsCatalog.detail.description');
  }

  get selectedWorkCycleName(): string {
    const workCycleId = this.displayedWorkAssignment?.workCycleId || '';
    if (!workCycleId) {
      return '-';
    }

    return this.workCycles.find((cycle) => cycle.workCycleId === workCycleId)?.name || workCycleId;
  }

  get selectedRestConfigurationName(): string {
    const restConfigurationId =
      this.displayedWorkAssignment?.restConfigurationId || this.workAssignmentForm.restConfigurationId;
    if (!restConfigurationId) {
      return '-';
    }

    return (
      this.restConfigurations.find(
        (restConfiguration) => restConfiguration.restConfigurationId === restConfigurationId
      )?.name || '-'
    );
  }

  get selectedAttendanceTrackingIntervalLabel(): string {
    const tracking = this.displayedAttendanceConfig?.tracking as
      | { trackingIntervalSeconds?: number; trackingIntervalMinutes?: number }
      | undefined;
    return formatAttendanceTrackingInterval(tracking?.trackingIntervalSeconds, tracking?.trackingIntervalMinutes);
  }

  get displayedWorkAssignment(): OrgUnitWorkAssignment | null {
    return this.workAssignmentForm.enabled
      ? this.selectedWorkAssignment
      : this.effectiveOperationalConfig?.workAssignment.value || null;
  }

  get displayedAttendanceConfig(): AttendanceConfigRecord | null {
    if (this.selectedAttendanceConfig && this.attendanceConfigForm.enabled) {
      return this.selectedAttendanceConfig;
    }
    const effective = this.effectiveOperationalConfig?.attendanceConfig;
    if (!effective?.value) {
      return null;
    }
    return {
      ...effective.value,
      attendanceConfigId: effective.attendanceConfigId || effective.value.attendanceConfigId || '',
    };
  }

  get workAssignmentOriginLabel(): string {
    return this.workAssignmentForm.enabled
      ? this.t('operationalConfigInheritance.own.orgUnit')
      : this.buildEffectiveOriginLabel(this.effectiveOperationalConfig?.workAssignment);
  }

  get attendanceConfigOriginLabel(): string {
    return this.attendanceConfigForm.enabled
      ? this.t('operationalConfigInheritance.own.orgUnit')
      : this.buildEffectiveOriginLabel(this.effectiveOperationalConfig?.attendanceConfig);
  }

  get isWorkAssignmentOwn(): boolean {
    return this.workAssignmentForm.enabled || (
      !this.isCreateMode &&
      this.effectiveOperationalConfig?.workAssignment.configFrom === 'orgUnit' &&
      this.effectiveOperationalConfig.workAssignment.configFromId === this.selectedOrgUnitId
    );
  }

  get isAttendanceConfigOwn(): boolean {
    return this.attendanceConfigForm.enabled || (
      !this.isCreateMode &&
      this.effectiveOperationalConfig?.attendanceConfig.configFrom === 'orgUnit' &&
      this.effectiveOperationalConfig.attendanceConfig.configFromId === this.selectedOrgUnitId
    );
  }

  get assignmentCyclePreviewDays(): WorkCyclePreviewDay[] {
    const cycle = this.selectedWorkCycleDetail;
    if (!cycle?.days?.length) {
      return [];
    }

    return [...cycle.days]
      .sort((left, right) => Number(left.position || 0) - Number(right.position || 0))
      .map((day, index) => {
        const schedule = day.dailyScheduleId
          ? this.dailySchedules.find((item) => item.dailyScheduleId === day.dailyScheduleId) || null
          : null;
        const isRest = day.dayType === 'rest' || !schedule;

        return {
          dayOrder: Number(day.position || index + 1),
          dayType: isRest ? 'rest' : 'work',
          scheduleName: isRest ? this.t('orgUnitsCatalog.workAssignment.cyclePreview.rest') : schedule?.name || '-',
          scheduleRange: isRest
            ? this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours')
            : this.getDailyScheduleRangeLabel(schedule),
          expectedHours: isRest
            ? this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours')
            : this.getDailyScheduleHoursLabel(schedule),
          isRest,
        };
      });
  }

  get assignmentScheduleMonthStarts(): Date[] {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    if (!start) {
      return [];
    }

    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    if (!end) {
      return Array.from({ length: this.assignmentScheduleMonthIndex + 1 }, (_, index) =>
        new Date(start.getFullYear(), start.getMonth() + index, 1)
      );
    }
    const months: Date[] = [];
    let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

    while (cursor <= lastMonth) {
      months.push(new Date(cursor));
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    return months;
  }

  get hasPreviousAssignmentScheduleMonth(): boolean {
    return this.assignmentScheduleMonthIndex > 0;
  }

  get hasNextAssignmentScheduleMonth(): boolean {
    if (!this.parseLocalDate(this.workAssignmentForm.effectiveTo) && !!this.getAssignmentCyclePreviewReferenceDate()) {
      return true;
    }
    return this.assignmentScheduleMonthIndex < this.assignmentScheduleMonthStarts.length - 1;
  }

  get assignmentSelectedScheduleMonth(): AssignmentScheduleCalendarMonth | null {
    const start = this.getAssignmentCyclePreviewReferenceDate();
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    const cycleDays = this.assignmentCyclePreviewDays;

    if (!start || !cycleDays.length) {
      return null;
    }

    const monthStarts = this.assignmentScheduleMonthStarts;
    const index = monthStarts.length
      ? Math.min(Math.max(this.assignmentScheduleMonthIndex, 0), monthStarts.length - 1)
      : 0;
    const monthDate = end
      ? monthStarts[index]
      : new Date(start.getFullYear(), start.getMonth() + Math.max(this.assignmentScheduleMonthIndex, 0), 1);

    return this.buildAssignmentScheduleCalendarMonth(monthDate, start, end, cycleDays);
  }

  get assignmentWeekdayLabels(): string[] {
    return this.translate.currentLang?.startsWith('en')
      ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
      : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  }

  get selectedAttendanceConfigSourcesLabel(): string {
    const sources = this.displayedAttendanceConfig?.attendanceCapture?.allowedSources || [];
    return sources.length ? sources.map((source) => this.t(getAttendanceSourceLabelKey(source))).join(', ') : '-';
  }

  get selectedAttendanceConfigZonesLabel(): string {
    const zoneIds = this.displayedAttendanceConfig?.zones?.allowedZoneIds || [];
    if (!zoneIds.length) {
      return '-';
    }

    return zoneIds
      .map((zoneId) => this.zoneOptions.find((zone) => zone.zoneId === zoneId)?.name || zoneId)
      .join(', ');
  }

  async refresh(): Promise<void> {
    this.invalidateAllCaches();
    await Promise.all([this.loadCompanies(true, true), this.loadOrgUnits(false)]);
    await this.loadSelectedOrgUnitDetail(false);
    await this.loadActiveTabData(false);
  }

  onCompanyChange(): void {
    this.companySelection.rememberSelection(this.selectedCompanyId);
    this.workspaceView = 'tree';
    this.searchTerm = '';
    this.resetUsersState();
    this.rebuildTree();
    this.clearDeferredTabState();
    void this.loadSelectedOrgUnitDetail(false);
    this.persistFilterState();
  }

  clearFilters(): void {
    if (!this.selectedCompanyId && !this.searchTerm) {
      return;
    }

    this.selectedCompanyId = this.companies.length === 1
      ? this.companySelection.resolveSelection(this.companies)
      : '';
    if (this.companies.length > 1) {
      this.companySelection.rememberSelection('');
    }
    this.companySearchTerm = this.selectedCompany?.name || '';
    this.searchTerm = '';
    this.selectedOrgUnitId = '';
    this.workspaceView = 'tree';
    this.clearPersistedFilterState();
    this.resetUsersState();
    this.openViewMode();
    this.rebuildTree();
    this.clearDeferredTabState();
    void this.loadSelectedOrgUnitDetail(false);
  }

  clearCompanySearch(): void {
    if (!this.companySearchTerm && !this.selectedCompanyId) {
      return;
    }

    this.companySearchTerm = '';
    if (this.selectedCompanyId) {
      this.selectedCompanyId = '';
      this.onCompanyChange();
    }
    this.persistFilterState();
  }

  clearSearchTerm(): void {
    if (!this.searchTerm) {
      return;
    }

    this.searchTerm = '';
    this.persistFilterState();
  }

  async selectOrgUnit(node: OrgUnitNode): Promise<void> {
    this.selectedOrgUnitId = this.selectedOrgUnitId === node.orgUnitId ? '' : node.orgUnitId;
    this.workspaceView = this.selectedOrgUnitId ? 'detail' : 'tree';
    this.resetUsersState();
    this.openViewMode();
    this.clearDeferredTabState();
    await this.loadSelectedOrgUnitDetail(false);
  }

  startCreate(): void {
    if (!this.canCreate || !this.selectedCompanyId) {
      return;
    }

    const selectedParentId = this.selectedOrgUnitId || '';

    this.workspaceView = 'detail';
    this.detailMode = 'create';
    this.activeDetailTab = 'general';
    this.form = {
      companiesRecordId: this.selectedCompanyId,
      code: '',
      name: '',
      type: '',
      parentId: selectedParentId || null,
    };
      void this.loadOrgUnitTypeOptions();
      this.selectedOrgUnitDetail = null;
      this.selectedWorkAssignment = null;
      this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
      this.selectedWorkCycleDetail = null;
      this.dailySchedules = [];
      this.assignmentScheduleMonthIndex = 0;
      this.devices = [];
    this.selectedDeviceIds = [];
    this.selectedAttendanceConfig = null;
    this.attendanceConfigForm = createEmptyAttendanceConfigForm();
    void this.restoreInheritedEffectiveConfig();
  }

  startEdit(): void {
    if (!this.canUpdate || !this.selectedOrgUnit) {
      return;
    }

    this.workspaceView = 'detail';
    this.detailMode = 'edit';
    this.activeDetailTab = 'general';
      this.form = {
      companiesRecordId: this.selectedOrgUnitDetail?.companiesRecordId || this.selectedOrgUnit.companiesRecordId,
      code: (this.selectedOrgUnitDetail?.code || this.selectedOrgUnit.code || '').toLowerCase(),
      name: this.selectedOrgUnitDetail?.name || this.selectedOrgUnit.name,
      type: this.selectedOrgUnitDetail?.type || this.selectedOrgUnit.type || '',
      parentId: this.selectedOrgUnitDetail?.parentId ?? this.selectedOrgUnit.parentId ?? null,
      };
      void this.loadOrgUnitTypeOptions();
      this.workAssignmentForm = this.mapWorkAssignmentToForm(this.selectedWorkAssignment);
      void this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
  }

  openViewMode(): void {
    this.detailMode = 'view';
    this.activeDetailTab = 'general';
    this.form = this.createEmptyForm();
    this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
    this.selectedWorkCycleDetail = null;
    this.assignmentScheduleMonthIndex = 0;
    this.selectedDeviceIds = [];
    this.attendanceConfigForm = createEmptyAttendanceConfigForm();
  }

  selectDetailTab(tab: DetailTab): void {
    this.activeDetailTab = tab;
    void this.loadActiveTabData();
  }

  onFormCompanyChange(): void {
    this.workAssignmentForm = this.createEmptyWorkAssignmentForm();
    this.selectedWorkCycleDetail = null;
    this.assignmentScheduleMonthIndex = 0;
    this.clearDeferredTabState();
    this.attendanceConfigForm.allowedZoneIds = [];
    void this.loadOrgUnitTypeOptions();
    void this.restoreInheritedEffectiveConfig();
  }

  onOrgUnitTypeInput(value: string): void {
    if (this.typeOptionsDebounceHandle) {
      clearTimeout(this.typeOptionsDebounceHandle);
    }

    this.typeOptionsDebounceHandle = setTimeout(() => {
      this.typeOptionsDebounceHandle = null;
      void this.loadOrgUnitTypeOptions(value);
    }, 250);
  }

  onOrgUnitCodeInput(value: string): void {
    this.form.code = (value || '').toLowerCase();
  }

  onOrgUnitTypeAutocompleteOpened(): void {
    void this.loadOrgUnitTypeOptions();
  }

  private async loadOrgUnitTypeOptions(like = ''): Promise<void> {
    const companiesRecordId = this.form.companiesRecordId;
    if (!companiesRecordId) {
      this.orgUnitTypeOptions = [];
      return;
    }

    const requestId = ++this.typeOptionsRequestId;
    this.isLoadingTypeOptions = true;
    try {
      const normalizedLike = String(like || '').trim();
      const response = await firstValueFrom(
        this.apiService.get<string[]>('org-units/type-options', {
          params: {
            companiesRecordId,
            ...(normalizedLike ? { like: normalizedLike } : {}),
          },
        })
      );

      if (requestId !== this.typeOptionsRequestId) {
        return;
      }

      this.orgUnitTypeOptions = Array.from(
        new Set((Array.isArray(response) ? response : []).map((type) => String(type).trim()).filter(Boolean))
      ).sort((left, right) => left.localeCompare(right));
    } catch (error) {
      if (requestId === this.typeOptionsRequestId) {
        console.error('Error loading org unit type options', error);
        this.orgUnitTypeOptions = [];
      }
    } finally {
      if (requestId === this.typeOptionsRequestId) {
        this.isLoadingTypeOptions = false;
      }
    }
  }

  onWorkAssignmentToggle(): void {
    if (!this.workAssignmentForm.enabled) {
      this.assignmentScheduleMonthIndex = 0;
      void this.restoreInheritedEffectiveConfig();
    }
  }

  async onWorkCycleChange(): Promise<void> {
    await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
    this.assignmentScheduleMonthIndex = 0;
  }

  onWorkAssignmentDatesChange(): void {
    this.assignmentScheduleMonthIndex = 0;
  }

  showPreviousAssignmentScheduleMonth(): void {
    if (this.hasPreviousAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex -= 1;
    }
  }

  showNextAssignmentScheduleMonth(): void {
    if (this.hasNextAssignmentScheduleMonth) {
      this.assignmentScheduleMonthIndex += 1;
    }
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  toPickerDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (!year || !month || !day) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  fromPickerDate(value: Date | null | undefined): string {
    if (!value || Number.isNaN(value.getTime())) {
      return '';
    }

    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onWorkAssignmentDateSelected(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    event: MatDatepickerInputEvent<Date>
  ): void {
    this.updateWorkAssignmentDate(target, this.fromPickerDate(event.value));
  }

  onWorkAssignmentDateChange(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    value: string
  ): void {
    this.updateWorkAssignmentDate(target, value);
  }

  onAttendanceConfigToggle(): void {
    if (this.attendanceConfigForm.enabled) {
      return;
    }

    void this.restoreInheritedEffectiveConfig();
  }

  onAttendanceAllowedSourcesChange(): void {
    for (const source of this.attendanceSourceRuleOptions) {
      if (!this.attendanceConfigForm.allowedSources.includes(source)) {
        this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
      }
    }
  }

  getAttendanceSourceRuleForm(source: string): AttendanceSourceRuleFormValue {
    return this.attendanceConfigForm.sourceRules[source];
  }

  isAttendanceSourceEnabled(source: string): boolean {
    return this.attendanceConfigForm.allowedSources.includes(source);
  }

  onAttendanceSourceRuleToggle(source: string): void {
    const rule = this.getAttendanceSourceRuleForm(source);
    if (!rule) {
      return;
    }

    if (rule.enabled) {
      this.attendanceConfigForm.sourceRules[source] = {
        ...rule,
        timezone: this.attendanceConfigForm.timezone,
        requireGeo: this.attendanceConfigForm.requireGeo,
        requireFaceEvidence: this.attendanceConfigForm.requireFaceEvidence,
        duplicateWindowSeconds: this.attendanceConfigForm.duplicateWindowSeconds,
        allowUntypedEvents: this.attendanceConfigForm.allowUntypedEvents,
        normalizeUntypedEvents: this.attendanceConfigForm.normalizeUntypedEvents,
        normalizeMistypedEvents: this.attendanceConfigForm.normalizeMistypedEvents,
      };
      return;
    }

    this.attendanceConfigForm.sourceRules[source] = createEmptyAttendanceSourceRuleForm(source, this.attendanceConfigForm);
  }

  attendanceSourceUsesTimezone(source: string): boolean { return usesTimezone(source); }
  attendanceSourceUsesGeo(source: string): boolean { return usesGeo(source); }
  attendanceSourceUsesDuplicateWindow(source: string): boolean { return usesDuplicateWindow(source); }
  attendanceSourceUsesUntypedNormalization(source: string): boolean { return usesUntypedNormalization(source); }
  attendanceSourceUsesMistypedNormalization(source: string): boolean { return usesMistypedNormalization(source); }
  attendanceSourceUsesDeviceRules(source: string): boolean { return usesDeviceRules(source); }

  async save(): Promise<void> {
    if (!this.canRead || (this.isCreateMode && !this.canCreate) || (this.isEditMode && !this.canUpdate)) {
      return;
    }

    const name = this.form.name.trim();
    const code = this.form.code.trim().toLowerCase();
    this.form.code = code;
    if (!name) {
      this.showNotification('snackbar-warning', this.t('orgUnitsCatalog.notifications.validationName'), 'bottom', 'center');
      return;
    }

    if (!this.form.companiesRecordId) {
      this.showNotification('snackbar-warning', this.t('orgUnitsCatalog.notifications.validationCompany'), 'bottom', 'center');
      return;
    }

    if (!this.form.type.trim()) {
      this.showNotification('snackbar-warning', this.t('orgUnitsCatalog.notifications.validationType'), 'bottom', 'center');
      return;
    }

    const workAssignmentPayload = this.buildWorkAssignmentPayload();
    if (this.workAssignmentForm.enabled && !workAssignmentPayload) {
      this.activeDetailTab = 'configuration';
      return;
    }

    if (this.canManageAttendanceConfig && this.attendanceConfigForm.enabled) {
      const attendanceConfigValidationError = this.validateAttendanceConfigForm();
      if (attendanceConfigValidationError) {
        this.activeDetailTab = 'attendanceConfiguration';
        this.showNotification('snackbar-warning', attendanceConfigValidationError, 'bottom', 'center');
        return;
      }
    }

    this.isSaving = true;
    try {
      const normalizedParentId = this.form.parentId ?? null;
      const parentPayload = { parentId: normalizedParentId };
      const codePayload = code ? { code } : {};

      if (this.isCreateMode) {
        const createEntityPayload = {
          companiesRecordId: this.form.companiesRecordId,
          name,
          type: this.form.type.trim(),
          ...codePayload,
          ...parentPayload,
        };

        const created = await firstValueFrom(
          this.apiService.post<OrgUnitResponseItem & { entity?: OrgUnitResponseItem }>('org-units/with-operational-config', {
            entity: createEntityPayload,
          })
        );
        const createdEntity = created?.entity || created;

        if ((workAssignmentPayload || this.selectedDeviceIds.length) && createdEntity?.orgUnitId) {
          await firstValueFrom(
            this.apiService.patch(`org-units/${createdEntity.orgUnitId}/with-operational-config`, {
              entity: {
                name,
                type: this.form.type.trim(),
                ...codePayload,
                ...parentPayload,
              },
              ...(workAssignmentPayload ? { workAssignment: workAssignmentPayload } : {}),
              deviceIds: [...this.selectedDeviceIds],
            })
          );
        }

        if (this.canManageAttendanceConfig && this.attendanceConfigForm.enabled && createdEntity?.orgUnitId) {
          const attendanceConfigPayload = buildAttendanceConfigPayload(this.attendanceConfigForm, {
            orgUnitId: createdEntity.orgUnitId,
          });
          if (attendanceConfigPayload) {
            await firstValueFrom(
              this.apiService.post<AttendanceConfigRecord>('attendance/configs', attendanceConfigPayload)
            );
          }
        }

        this.showNotification('snackbar-success', this.t('orgUnitsCatalog.notifications.created'), 'bottom', 'center');
        this.selectedCompanyId = createdEntity?.companiesRecordId || this.selectedCompanyId;
        this.invalidateAllCaches();
        await this.loadOrgUnits(false);
        this.selectedOrgUnitId = createdEntity?.orgUnitId || this.findByName(name)?.orgUnitId || '';
        await this.loadSelectedOrgUnitDetail(false);
        this.openViewMode();
        return;
      }

      if (!this.selectedOrgUnitId) {
        return;
      }

      await firstValueFrom(
        this.apiService.patch(`org-units/${this.selectedOrgUnitId}`, {
          name,
          type: this.form.type.trim(),
          ...codePayload,
          isActive: this.selectedOrgUnitDetail?.isActive ?? this.selectedOrgUnit?.isActive ?? true,
          ...parentPayload,
        })
      );

      await firstValueFrom(
        this.apiService.patch(`org-units/${this.selectedOrgUnitId}/with-operational-config`, {
          entity: {
            name,
            type: this.form.type.trim(),
            ...codePayload,
            ...parentPayload,
          },
          ...(workAssignmentPayload
            ? { workAssignment: workAssignmentPayload }
            : this.selectedWorkAssignment
              ? { workAssignment: null }
              : {}),
          deviceIds: [...this.selectedDeviceIds],
        })
      );

      await this.persistAttendanceConfig();

      this.showNotification('snackbar-success', this.t('orgUnitsCatalog.notifications.updated'), 'bottom', 'center');
      this.invalidateAllCaches();
      await this.loadOrgUnits(false);
      await this.loadSelectedOrgUnitDetail(false);
      this.openViewMode();
    } catch (error) {
      console.error('Error saving org unit', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  async deleteSelected(): Promise<void> {
    if (!this.canDelete || !this.selectedOrgUnit) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('orgUnitsCatalog.dialog.deleteTitle'),
        message: this.t('orgUnitsCatalog.dialog.deleteMessage', { unit: this.selectedOrgUnit.name }),
        confirmLabel: this.t('orgUnitsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.isDeleting = true;
    const deletedId = this.selectedOrgUnit.orgUnitId;

    try {
      await firstValueFrom(this.apiService.delete(`org-units/${deletedId}`));
      this.showNotification('snackbar-success', this.t('orgUnitsCatalog.notifications.deleted'), 'bottom', 'center');
      this.invalidateAllCaches();
      await this.loadOrgUnits(false);
      if (this.selectedOrgUnitId === deletedId) {
        this.selectedOrgUnitId = this.companyOrgUnits[0]?.orgUnitId || '';
      }
      this.openViewMode();
    } catch (error) {
      console.error('Error deleting org unit', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.deleteError')),
        'bottom',
        'center'
      );
    } finally {
      this.isDeleting = false;
    }
  }

  getNodeTypeLabel(type?: string): string {
    return type?.trim() || this.t('orgUnitsCatalog.noType');
  }

  getLevelLabel(level: number): string {
    return this.t('orgUnitsCatalog.levelLabel', { level });
  }

  getOrgUnitStatusLabel(isActive: boolean): string {
    return isActive ? this.t('common.status.active') : this.t('common.status.inactive');
  }

  getUserStatusLabel(status?: string): string {
    return status === 'active' ? this.t('common.status.active') : status || this.t('common.status.inactive');
  }

  getUserRolesLabel(user: OrgUnitUser): string {
    return user.roles?.length ? user.roles.join(', ') : this.t('orgUnitsCatalog.users.noRoles');
  }

  getNodeIcon(type?: string): string {
    const normalizedType = (type || '').trim().toLowerCase();

    if (normalizedType.includes('gerenc') || normalizedType.includes('management')) {
      return 'badge';
    }

    if (normalizedType.includes('sistem') || normalizedType.includes('it') || normalizedType.includes('tech')) {
      return 'desktop_windows';
    }

    if (
      normalizedType.includes('depa') ||
      normalizedType.includes('produc') ||
      normalizedType.includes('operac') ||
      normalizedType.includes('ventas')
    ) {
      return 'account_tree';
    }

    if (normalizedType.includes('conta') || normalizedType.includes('tesor')) {
      return 'apartment';
    }

    return 'business';
  }

  canDropInto(target: OrgUnitNode): boolean {
    if (!this.draggedOrgUnitId || this.draggedOrgUnitId === target.orgUnitId) {
      return false;
    }

    const draggedNode = this.findNodeById(this.companyOrgUnits, this.draggedOrgUnitId);
    if (!draggedNode) {
      return false;
    }

    const descendantIds = new Set(this.flattenNodes([draggedNode]).map((node) => node.orgUnitId));
    return !descendantIds.has(target.orgUnitId);
  }

  handleDragStart(node: OrgUnitNode): void {
    if (!this.canUpdate) {
      return;
    }

    this.draggedOrgUnitId = node.orgUnitId;
    this.dragOverOrgUnitId = '';
    this.isRootDropActive = false;
  }

  handleDragEnd(): void {
    this.draggedOrgUnitId = '';
    this.dragOverOrgUnitId = '';
    this.isRootDropActive = false;
  }

  handleDragOverNode(event: DragEvent, target: OrgUnitNode): void {
    if (!this.canDropInto(target)) {
      return;
    }

    event.preventDefault();
    this.dragOverOrgUnitId = target.orgUnitId;
    this.isRootDropActive = false;
  }

  async handleDropOnNode(event: DragEvent, target: OrgUnitNode): Promise<void> {
    event.preventDefault();

    if (!this.canDropInto(target)) {
      this.handleDragEnd();
      return;
    }

    await this.moveOrgUnit(this.draggedOrgUnitId, target.orgUnitId);
  }

  handleRootDragOver(event: DragEvent): void {
    if (!this.draggedOrgUnitId) {
      return;
    }

    event.preventDefault();
    this.isRootDropActive = true;
    this.dragOverOrgUnitId = '';
  }

  async handleRootDrop(event: DragEvent): Promise<void> {
    event.preventDefault();

    if (!this.draggedOrgUnitId) {
      return;
    }

    await this.moveOrgUnit(this.draggedOrgUnitId, null);
  }

  isCollapsed(node: OrgUnitNode): boolean {
    return this.collapsedNodeIds.has(node.orgUnitId);
  }

  toggleNode(node: OrgUnitNode, event?: Event): void {
    event?.stopPropagation();

    if (!node.children.length) {
      return;
    }

    if (this.collapsedNodeIds.has(node.orgUnitId)) {
      this.collapsedNodeIds.delete(node.orgUnitId);
      return;
    }

    this.collapsedNodeIds.add(node.orgUnitId);
  }

  trackByCompany(_: number, company: CompanyRecord): string {
    return company.companiesRecordId;
  }

  handleCompanyInputChange(value: string): void {
    this.showAllCompanyOptions = false;
    this.companySearchTerm = value;
    if (this.selectedCompanyId && value !== this.selectedCompany?.name) {
      this.selectedCompanyId = '';
      this.onCompanyChange();
    }
    this.persistFilterState();
  }

  handleCompanyAutocompleteOpened(): void {
    this.showAllCompanyOptions = true;
  }

  handleCompanyAutocompleteClosed(): void {
    this.showAllCompanyOptions = false;
  }

  handleCompanyOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const optionValue = event.option.value as CompanyRecord | '__empty__' | null;
    if (!optionValue || optionValue === '__empty__') {
      this.clearCompanySearch();
      return;
    }

    this.selectedCompanyId = optionValue.companiesRecordId;
    this.companySearchTerm = optionValue.name;
    this.onCompanyChange();
  }

  private restoreFilterState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      const rawState = sessionStorage.getItem(OrgUnitsComponent.FILTER_STATE_KEY);
      if (!rawState) {
        return;
      }

      const state = JSON.parse(rawState) as Partial<{
        selectedCompanyId: string;
        companySearchTerm: string;
        searchTerm: string;
      }>;
      this.selectedCompanyId = typeof state.selectedCompanyId === 'string' ? state.selectedCompanyId : '';
      this.companySearchTerm = typeof state.companySearchTerm === 'string' ? state.companySearchTerm : '';
      this.searchTerm = typeof state.searchTerm === 'string' ? state.searchTerm : '';
    } catch {
      this.clearPersistedFilterState();
    }
  }

  persistFilterState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    sessionStorage.setItem(OrgUnitsComponent.FILTER_STATE_KEY, JSON.stringify({
      selectedCompanyId: this.selectedCompanyId,
      companySearchTerm: this.getCompanySearchText(this.companySearchTerm),
      searchTerm: this.searchTerm,
    }));
  }

  private clearPersistedFilterState(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(OrgUnitsComponent.FILTER_STATE_KEY);
    }
  }

  displayCompanyOption = (value: string | CompanyRecord | null): string =>
    this.getCompanySearchText(value);

  trackByNode(_: number, node: OrgUnitNode): string {
    return node.orgUnitId;
  }

  trackByLevel(_: number, level: LevelSummary): number {
    return level.level;
  }

  trackByUser(_: number, user: OrgUnitUser): string {
    return user.uid;
  }

  async addSelectedUserToAllow(): Promise<void> {
    if (!this.selectedAllowUserId) {
      return;
    }

    await this.updateUserAssignment(this.selectedAllowUserId, 'allow', true);
    this.selectedAllowUserId = '';
  }

  async addSelectedUserToDeny(): Promise<void> {
    if (!this.selectedDenyUserId) {
      return;
    }

    await this.updateUserAssignment(this.selectedDenyUserId, 'deny', true);
    this.selectedDenyUserId = '';
  }

  async removeUserFromAllow(user: OrgUnitUser): Promise<void> {
    await this.updateUserAssignment(user.uid, 'allow', false);
  }

  async removeUserFromDeny(user: OrgUnitUser): Promise<void> {
    await this.updateUserAssignment(user.uid, 'deny', false);
  }

  private async loadCompanies(preserveSelection = true, forceRefresh = false): Promise<void> {
    this.isLoadingCompanies = true;
    try {
      const response = await this.companyDirectory.getAllActive(forceRefresh);
      this.companies = response
        .filter((company) => company.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      const rememberedCompanyId = preserveSelection ? this.companySelection.getRememberedSelection() : '';
      this.selectedCompanyId = this.companySelection.resolveSelection(
        this.companies,
        rememberedCompanyId || (preserveSelection ? this.selectedCompanyId : '')
      );
      this.companySearchTerm = this.selectedCompany?.name || '';
    } catch (error) {
      console.error('Error loading companies', error);
      this.companies = [];
      this.selectedCompanyId = '';
      this.companySearchTerm = '';
      this.showNotification('snackbar-danger', this.t('orgUnitsCatalog.notifications.loadCompaniesError'), 'bottom', 'center');
    } finally {
      this.isLoadingCompanies = false;
    }
  }

  private async loadSelectedOrgUnitDetail(showError = true): Promise<void> {
    if (!this.selectedOrgUnitId) {
      this.effectiveOperationalConfigRequestId += 1;
      this.selectedOrgUnitDetail = null;
      this.selectedWorkAssignment = null;
      this.selectedAttendanceConfig = null;
      this.effectiveOperationalConfig = null;
      return;
    }

    this.isLoadingDetail = true;
    try {
      const cachedResponse = this.getCachedValue(this.detailCache, this.selectedOrgUnitId);
      const response = cachedResponse || await firstValueFrom(
        this.apiService.get<OrgUnitLaborConfigResponse>(`org-units/${this.selectedOrgUnitId}/with-operational-config`)
      );
      if (!cachedResponse) {
        this.setCachedValue(this.detailCache, this.selectedOrgUnitId, response);
      }

      this.applySelectedOrgUnitDetail(response);
      await this.loadEffectiveOperationalConfig('orgUnit', this.selectedOrgUnitId);
    } catch (error) {
      console.error('Error loading org unit labor config', error);
      this.selectedOrgUnitDetail = null;
      this.selectedWorkAssignment = null;
      this.selectedWorkCycleDetail = null;
      this.selectedDeviceIds = [];
      if (showError) {
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.loadDetailError')),
          'bottom',
          'center'
        );
      }
    } finally {
      this.isLoadingDetail = false;
    }
  }

  private applySelectedOrgUnitDetail(response: OrgUnitLaborConfigResponse): void {
      const detailEntity = response?.entity || null;
      const treeNode = this.findNodeById(this.companyOrgUnits, this.selectedOrgUnitId);
      this.selectedOrgUnitDetail = detailEntity
        ? {
            ...detailEntity,
            parentId: detailEntity.parentId ?? null,
            level: treeNode?.level ?? detailEntity.level ?? 0,
          }
        : null;
      this.selectedWorkAssignment = response?.workAssignment || null;
      this.selectedDeviceIds = (response?.devices || [])
        .map((device) => device.deviceId)
        .filter((deviceId): deviceId is string => !!deviceId);

      if (this.isEditMode) {
        this.workAssignmentForm = this.mapWorkAssignmentToForm(this.selectedWorkAssignment);
        void this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      }
  }

  private async restoreInheritedEffectiveConfig(): Promise<void> {
    const selectedNode = this.isCreateMode
      ? this.findNodeById(this.companyOrgUnits, this.form.parentId || '')
      : this.findNodeById(this.companyOrgUnits, this.selectedOrgUnitId);
    const parentId = this.isCreateMode ? this.form.parentId : selectedNode?.parentId;
    if (parentId) {
      await this.loadEffectiveOperationalConfig('orgUnit', parentId);
      return;
    }
    const companyId = this.form.companiesRecordId || this.selectedCompanyId;
    if (companyId) {
      await this.loadEffectiveOperationalConfig('companiesRecord', companyId);
      return;
    }
    this.effectiveOperationalConfigRequestId += 1;
    this.effectiveOperationalConfig = null;
    this.applyEffectiveOperationalConfig();
  }

  private async loadEffectiveOperationalConfig(
    elementType: 'companiesRecord' | 'orgUnit',
    elementId: string
  ): Promise<void> {
    const requestId = ++this.effectiveOperationalConfigRequestId;
    try {
      const effectiveOperationalConfig = await firstValueFrom(
        this.apiService.get<EffectiveOperationalConfigResponse>(
          `operational-config/effective/${elementType}/${encodeURIComponent(elementId)}`
        )
      );
      if (requestId !== this.effectiveOperationalConfigRequestId) {
        return;
      }
      this.effectiveOperationalConfig = effectiveOperationalConfig;
      this.applyEffectiveOperationalConfig();
    } catch (error) {
      if (requestId !== this.effectiveOperationalConfigRequestId) {
        return;
      }
      console.error('Error loading effective operational config for org unit', error);
      this.effectiveOperationalConfig = null;
      this.applyEffectiveOperationalConfig();
    }
  }

  private applyEffectiveOperationalConfig(): void {
    if (!this.workAssignmentForm.enabled) {
      this.workAssignmentForm = this.mapWorkAssignmentToForm(
        this.effectiveOperationalConfig?.workAssignment.value || null
      );
      this.workAssignmentForm.enabled = false;
      void this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      this.assignmentScheduleMonthIndex = 0;
    }
    if (!this.attendanceConfigForm.enabled) {
      this.attendanceConfigForm = mapAttendanceConfigToForm(this.displayedAttendanceConfig);
      this.attendanceConfigForm.enabled = false;
    }
  }

  private buildEffectiveOriginLabel(
    block?: { configFrom: string; configFromId?: string | null } | null
  ): string {
    if (!block) {
      return this.t('operationalConfigInheritance.unavailable');
    }
    const sourceId = block.configFromId || '';
    if (block.configFrom === 'orgUnit') {
      const name = this.findNodeById(this.companyOrgUnits, sourceId)?.name || sourceId;
      return !this.isCreateMode && sourceId === this.selectedOrgUnitId
        ? this.t('operationalConfigInheritance.own.orgUnit')
        : this.t('operationalConfigInheritance.inherited.orgUnit', { name });
    }
    if (block.configFrom === 'companiesRecord') {
      const name = this.companies.find((company) => company.companiesRecordId === sourceId)?.name || sourceId;
      return this.t('operationalConfigInheritance.inherited.company', { name });
    }
    if (block.configFrom === 'center') {
      return this.t('operationalConfigInheritance.inherited.center');
    }
    return this.t('operationalConfigInheritance.inherited.defaults');
  }

  private async loadActiveTabData(showError = true): Promise<void> {
    const companyId = this.isCreateMode || this.isEditMode
      ? this.form.companiesRecordId
      : this.selectedCompanyId;

    if (this.activeDetailTab === 'configuration') {
      await Promise.all([
        this.loadWorkCycles(companyId, showError),
        this.loadDailySchedules(companyId, showError),
        this.loadRestConfigurations(companyId, showError),
      ]);
      await this.loadSelectedWorkCycleDetail(this.workAssignmentForm.workCycleId);
      return;
    }

    if (this.activeDetailTab === 'devices') {
      await this.loadDevices(companyId, showError);
      return;
    }

    if (this.activeDetailTab === 'attendanceConfiguration') {
      await Promise.all([
        this.loadAttendanceConfigForSelectedOrgUnit(showError),
        this.loadZones(companyId, showError),
      ]);
      return;
    }

    if (this.activeDetailTab === 'users') {
      await this.loadUsersData(showError);
    }
  }

  private async loadAttendanceConfigForSelectedOrgUnit(showError = true): Promise<void> {
    if (this.isCreateMode) {
      this.selectedAttendanceConfig = null;
      if (!this.attendanceConfigForm.enabled) {
        this.attendanceConfigForm = createEmptyAttendanceConfigForm();
        this.applyEffectiveOperationalConfig();
      }
      return;
    }

    if (!this.selectedOrgUnitId || !this.canReadAttendanceConfig) {
      this.selectedAttendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    const cached = this.getCachedValue(this.attendanceConfigCache, this.selectedOrgUnitId);
    if (cached !== undefined) {
      this.selectedAttendanceConfig = cached;
      this.attendanceConfigForm = mapAttendanceConfigToForm(cached);
      this.applyEffectiveOperationalConfig();
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceConfigListResponse>('attendance/configs', {
          params: { orgUnitId: this.selectedOrgUnitId },
        })
      );
      const config = findAttendanceConfigForScope(response, { orgUnitId: this.selectedOrgUnitId });
      this.selectedAttendanceConfig = config;
      this.attendanceConfigForm = mapAttendanceConfigToForm(config);
      this.applyEffectiveOperationalConfig();
      this.setCachedValue(this.attendanceConfigCache, this.selectedOrgUnitId, config);
    } catch (error) {
      this.selectedAttendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      this.applyEffectiveOperationalConfig();
      if (showError) {
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('attendanceConfigsCatalog.notifications.loadError')),
          'bottom',
          'center'
        );
      }
    }
  }

  private getCachedValue<T>(cache: Map<string, CacheEntry<T>>, key: string): T | undefined {
    const entry = cache.get(key);
    if (!entry) {
      return undefined;
    }
    if (entry.expiresAt <= Date.now()) {
      cache.delete(key);
      return undefined;
    }
    return entry.value;
  }

  private setCachedValue<T>(cache: Map<string, CacheEntry<T>>, key: string, value: T): void {
    cache.set(key, { value, expiresAt: Date.now() + OrgUnitsComponent.CACHE_TTL_MS });
  }

  private clearDeferredTabState(): void {
    this.workCycles = [];
    this.dailySchedules = [];
    this.restConfigurations = [];
    this.devices = [];
    this.zoneOptions = [];
    this.selectedAttendanceConfig = null;
    this.attendanceConfigForm = createEmptyAttendanceConfigForm();
  }

  private invalidateAllCaches(): void {
    this.workCyclesCache.clear();
    this.dailySchedulesCache.clear();
    this.restConfigurationsCache.clear();
    this.devicesCache.clear();
    this.zonesCache.clear();
    this.detailCache.clear();
    this.attendanceConfigCache.clear();
    this.usersCache.clear();
  }

  private async loadWorkCycles(companyId: string, showError = true): Promise<void> {
    if (!companyId) {
      this.workCycles = [];
      this.selectedWorkCycleDetail = null;
      return;
    }

    const cached = this.getCachedValue(this.workCyclesCache, companyId);
    if (cached) {
      this.workCycles = cached;
      return;
    }

    this.isLoadingWorkCycles = true;
    try {
        const response = await firstValueFrom(
          this.apiService.get<WorkCycleConfig[] | WorkCyclesResponse>('labor-config/work-cycles', {
            params: { companiesRecordId: companyId },
          })
        );

      this.workCycles = this.normalizeWorkCycles(response)
        .filter((cycle) => cycle.isActive !== false)
        .sort((left, right) => left.name.localeCompare(right.name));
      this.setCachedValue(this.workCyclesCache, companyId, this.workCycles);
      } catch (error) {
        console.error('Error loading work cycles for org unit', error);
        this.workCycles = [];
        this.selectedWorkCycleDetail = null;
        if (showError) {
          this.showNotification('snackbar-danger', this.t('orgUnitsCatalog.notifications.loadWorkCyclesError'), 'bottom', 'center');
        }
    } finally {
      this.isLoadingWorkCycles = false;
    }
  }

  private async loadDailySchedules(companyId: string, showError = true): Promise<void> {
    if (!companyId) {
      this.dailySchedules = [];
      return;
    }

    const cached = this.getCachedValue(this.dailySchedulesCache, companyId);
    if (cached) {
      this.dailySchedules = cached;
      return;
    }

    try {
      this.dailySchedules =
        (
          await firstValueFrom(
            this.apiService.get<DailyScheduleConfig[]>('labor-config/daily-schedules', {
              params: { companiesRecordId: companyId, isActive: 'true' },
            })
          )
        )
          ?.filter((schedule) => schedule.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];
      this.setCachedValue(this.dailySchedulesCache, companyId, this.dailySchedules);
    } catch (error) {
      console.error('Error loading daily schedules for org unit', error);
      this.dailySchedules = [];
      if (showError) {
        this.showNotification('snackbar-danger', this.t('orgUnitsCatalog.notifications.loadWorkCyclesError'), 'bottom', 'center');
      }
    }
  }

  private async loadSelectedWorkCycleDetail(workCycleId: string): Promise<void> {
    if (!workCycleId) {
      this.selectedWorkCycleDetail = null;
      return;
    }

    const baseCycle = this.workCycles.find((cycle) => cycle.workCycleId === workCycleId) || null;
    if (baseCycle?.days?.length) {
      this.selectedWorkCycleDetail = baseCycle;
      return;
    }

    try {
      this.selectedWorkCycleDetail = await firstValueFrom(
        this.apiService.get<WorkCycleConfig>(`labor-config/work-cycles/${workCycleId}`)
      );
    } catch (error) {
      console.error('Error loading selected work cycle detail for org unit', error);
      this.selectedWorkCycleDetail = baseCycle;
    }
  }

  private async loadRestConfigurations(companyId: string, showError = true): Promise<void> {
    if (!companyId) {
      this.restConfigurations = [];
      return;
    }

    const cached = this.getCachedValue(this.restConfigurationsCache, companyId);
    if (cached) {
      this.restConfigurations = cached;
      return;
    }

    try {
      this.restConfigurations =
        (
          await firstValueFrom(
            this.apiService.get<RestConfigurationConfig[]>('labor-config/rest-configurations', {
              params: { companiesRecordId: companyId, isActive: 'true' },
            })
          )
        )
          ?.filter((restConfiguration) => restConfiguration.isActive !== false)
          .sort((left, right) => left.name.localeCompare(right.name)) || [];
      this.setCachedValue(this.restConfigurationsCache, companyId, this.restConfigurations);

      if (
        this.workAssignmentForm.restConfigurationId &&
        !this.restConfigurations.some(
          (restConfiguration) => restConfiguration.restConfigurationId === this.workAssignmentForm.restConfigurationId
        )
      ) {
        this.workAssignmentForm.restConfigurationId = '';
      }
    } catch (error) {
      console.error('Error loading rest configurations for org unit', error);
      this.restConfigurations = [];
      if (showError) {
        this.showNotification('snackbar-danger', this.t('orgUnitsCatalog.notifications.loadWorkCyclesError'), 'bottom', 'center');
      }
    }
  }

  private async loadDevices(companyId: string, showError = true): Promise<void> {
    if (!companyId) {
      this.devices = [];
      this.selectedDeviceIds = [];
      return;
    }

    const cached = this.getCachedValue(this.devicesCache, companyId);
    if (cached) {
      this.devices = cached;
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DevicesResponse>('devices', {
          params: {
            companiesRecordId: companyId,
            perPage: 200,
            page: 1,
          },
        })
      );

      this.devices = (response?.data || []).sort((left, right) =>
        (left.name || left.serial || '').localeCompare(right.name || right.serial || '')
      );
      this.setCachedValue(this.devicesCache, companyId, this.devices);
      this.selectedDeviceIds = this.selectedDeviceIds.filter((deviceId) =>
        this.devices.some((device) => device.deviceId === deviceId)
      );
    } catch (error) {
      console.error('Error loading devices for org unit', error);
      this.devices = [];
      this.selectedDeviceIds = [];
      if (showError) {
        this.showNotification('snackbar-danger', this.t('devicesCatalog.notifications.loadError'), 'bottom', 'center');
      }
    }
  }

  private async loadZones(companyId: string, showError = true): Promise<void> {
    if (!companyId) {
      this.zoneOptions = [];
      return;
    }

    const cached = this.getCachedValue(this.zonesCache, companyId);
    if (cached) {
      this.zoneOptions = cached;
      return;
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceZoneListResponse>('attendance/zones', {
          params: {
            companiesRecordId: companyId,
            isActive: 'true',
            perPage: 200,
            page: 1,
          },
        })
      );
      this.zoneOptions = extractAttendanceZones(response).filter((zone) => zone.isActive !== false);
      this.setCachedValue(this.zonesCache, companyId, this.zoneOptions);
    } catch (error) {
      console.error('Error loading attendance zones for org unit', error);
      this.zoneOptions = [];
      if (showError) {
        this.showNotification('snackbar-danger', this.t('attendanceConfigsCatalog.notifications.loadZonesError'), 'bottom', 'center');
      }
    }
  }

  private async loadOrgUnits(showError = true): Promise<void> {
    this.isLoadingOrgUnits = true;
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitResponseItem[] | OrgUnitsResponse>('org-units'));

      this.allOrgUnits = this.normalizeOrgUnits(response)
        .filter((unit) => Boolean(unit.orgUnitId))
        .sort((left, right) => {
          const levelDiff = (left.level ?? 0) - (right.level ?? 0);
          return levelDiff !== 0 ? levelDiff : left.name.localeCompare(right.name);
        });

      this.rebuildTree();
    } catch (error) {
      console.error('Error loading org units', error);
      this.allOrgUnits = [];
      this.companyOrgUnits = [];
      this.levelSummaries = [];
      if (showError) {
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.loadError')),
          'bottom',
          'center'
        );
      }
    } finally {
      this.isLoadingOrgUnits = false;
    }
  }

  private rebuildTree(): void {
    const units = this.allOrgUnits.filter((unit) => unit.companiesRecordId === this.selectedCompanyId);
    this.companyOrgUnits = this.buildTree(units);
    this.levelSummaries = this.buildLevelSummaries(this.companyOrgUnits);

    const selectedStillExists = this.findNodeById(this.companyOrgUnits, this.selectedOrgUnitId);
    this.selectedOrgUnitId = selectedStillExists?.orgUnitId || this.companyOrgUnits[0]?.orgUnitId || '';

    if (this.isCreateMode) {
      this.form.companiesRecordId = this.selectedCompanyId;
    }

    if (!this.selectedOrgUnitId) {
      this.selectedOrgUnitDetail = null;
      this.selectedWorkAssignment = null;
      this.selectedAttendanceConfig = null;
      this.resetUsersState();
    }
  }

  private normalizeCompanies(response: CompanyRecord[] | CompaniesResponse): CompanyRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response.data || response.items || response.results || response.companiesRecords || [];
  }

  private normalizeOrgUnits(response: OrgUnitResponseItem[] | OrgUnitsResponse): OrgUnitResponseItem[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response.data || response.items || response.results || response.orgUnits || [];
  }

  private normalizeWorkCycles(response: WorkCycleConfig[] | WorkCyclesResponse): WorkCycleConfig[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response.data || response.items || response.results || response.workCycles || [];
  }

  private buildTree(units: OrgUnitResponseItem[]): OrgUnitNode[] {
    const nodeMap = new Map<string, OrgUnitNode>();
    const roots: OrgUnitNode[] = [];

    for (const unit of units) {
      nodeMap.set(unit.orgUnitId, {
        ...unit,
        level: unit.level ?? 0,
        isActive: unit.isActive !== false,
        children: [],
      });
    }

    for (const node of nodeMap.values()) {
      const parentId = node.parentId || '';
      const parent = parentId ? nodeMap.get(parentId) : undefined;
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }

    const sortNodes = (nodes: OrgUnitNode[]): void => {
      nodes.sort((left, right) => left.name.localeCompare(right.name));
      nodes.forEach((node) => sortNodes(node.children));
    };

    const assignLevels = (nodes: OrgUnitNode[], level = 0): void => {
      nodes.forEach((node) => {
        node.level = level;
        assignLevels(node.children, level + 1);
      });
    };

    sortNodes(roots);
    assignLevels(roots);
    return roots;
  }

  private buildLevelSummaries(nodes: OrgUnitNode[]): LevelSummary[] {
    const summaryMap = new Map<number, number>();
    for (const node of this.flattenNodes(nodes)) {
      summaryMap.set(node.level, (summaryMap.get(node.level) || 0) + 1);
    }

    return [...summaryMap.entries()]
      .sort((left, right) => left[0] - right[0])
      .map(([level, count]) => ({ level, count }));
  }

  private flattenNodes(nodes: OrgUnitNode[]): OrgUnitNode[] {
    return nodes.flatMap((node) => [node, ...this.flattenNodes(node.children)]);
  }

  private findNodeById(nodes: OrgUnitNode[], nodeId: string): OrgUnitNode | undefined {
    for (const node of nodes) {
      if (node.orgUnitId === nodeId) {
        return node;
      }

      const childMatch = this.findNodeById(node.children, nodeId);
      if (childMatch) {
        return childMatch;
      }
    }

    return undefined;
  }

  private findByName(name: string): OrgUnitNode | undefined {
    return this.flattenNodes(this.companyOrgUnits).find((unit) => unit.name === name);
  }

  private async moveOrgUnit(orgUnitId: string, parentId: string | null): Promise<void> {
    const node = this.findNodeById(this.companyOrgUnits, orgUnitId);
    if (!node || !this.canUpdate) {
      this.handleDragEnd();
      return;
    }

    this.isMoving = true;
    try {
      const normalizedParentId = parentId ?? null;
      const parentPayload = { parentId: normalizedParentId };

      await firstValueFrom(
        this.apiService.patch(`org-units/${orgUnitId}`, {
          name: node.name,
          type: node.type || '',
          isActive: node.isActive !== false,
          ...parentPayload,
        })
      );

      this.showNotification('snackbar-success', this.t('orgUnitsCatalog.notifications.moved'), 'bottom', 'center');
      this.invalidateAllCaches();
      await this.loadOrgUnits(false);
      this.selectedOrgUnitId = orgUnitId;
    } catch (error) {
      console.error('Error moving org unit', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.moveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isMoving = false;
      this.handleDragEnd();
    }
  }

  private async loadUsersData(showError = true): Promise<void> {
    if (!this.selectedOrgUnit) {
      this.resetUsersState();
      return;
    }

    const cacheKey = this.selectedOrgUnit.orgUnitId;
    const cached = this.getCachedValue(this.usersCache, cacheKey);
    if (cached) {
      this.allowUsers = cached.allow;
      this.denyUsers = cached.deny;
      this.availableUsers = cached.available;
      return;
    }

    this.isLoadingUsers = true;
    try {
      const [usersResponse, availableResponse] = await Promise.all([
        firstValueFrom(this.apiService.get<OrgUnitUsersResponse>(`org-units/${this.selectedOrgUnit.orgUnitId}/users`)),
        firstValueFrom(
          this.apiService.get<OrgUnitUser[]>(
            `org-units/companies-records/${this.selectedOrgUnit.companiesRecordId}/users/available`
          )
        ),
      ]);

      this.allowUsers = usersResponse?.allow || [];
      this.denyUsers = usersResponse?.deny || [];
      this.availableUsers = Array.isArray(availableResponse) ? availableResponse : [];
      this.setCachedValue(this.usersCache, cacheKey, {
        allow: this.allowUsers,
        deny: this.denyUsers,
        available: this.availableUsers,
      });
    } catch (error) {
      console.error('Error loading org unit users', error);
      this.resetUsersState();
      if (showError) {
        this.showNotification('snackbar-danger', this.t('orgUnitsCatalog.notifications.loadUsersError'), 'bottom', 'center');
      }
    } finally {
      this.isLoadingUsers = false;
    }
  }

  private async updateUserAssignment(uid: string, listType: 'allow' | 'deny', enabled: boolean): Promise<void> {
    if (!this.selectedOrgUnit || !this.canUpdate) {
      return;
    }

    this.isSavingUsers = true;
    try {
      await firstValueFrom(
        this.apiService.patch(`org-units/${this.selectedOrgUnit.orgUnitId}/users/${uid}/${listType}/${enabled}`, {})
      );

      const notificationKey =
        listType === 'allow'
          ? enabled
            ? 'orgUnitsCatalog.notifications.allowAdded'
            : 'orgUnitsCatalog.notifications.allowRemoved'
          : enabled
            ? 'orgUnitsCatalog.notifications.denyAdded'
            : 'orgUnitsCatalog.notifications.denyRemoved';

      this.showNotification('snackbar-success', this.t(notificationKey), 'bottom', 'center');
      this.usersCache.delete(this.selectedOrgUnit.orgUnitId);
      await this.loadUsersData(false);
    } catch (error) {
      console.error('Error updating org unit user assignment', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('orgUnitsCatalog.notifications.saveUsersError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSavingUsers = false;
    }
  }

  private filterTree(nodes: OrgUnitNode[], term: string): OrgUnitNode[] {
    const filteredNodes: OrgUnitNode[] = [];
    for (const node of nodes) {
      const filteredChildren = this.filterTree(node.children, term);
      const matches =
        node.name.toLowerCase().includes(term) ||
        node.code.toLowerCase().includes(term) ||
        this.getNodeTypeLabel(node.type).toLowerCase().includes(term);

      if (matches || filteredChildren.length) {
        filteredNodes.push({ ...node, children: filteredChildren });
      }
    }

    return filteredNodes;
  }

  private createEmptyForm(): OrgUnitForm {
    return {
      companiesRecordId: this.selectedCompanyId,
      code: '',
      name: '',
      type: '',
      parentId: null,
    };
  }

  private createEmptyWorkAssignmentForm(): WorkAssignmentForm {
    return {
      enabled: false,
      workCycleId: '',
      restConfigurationId: '',
      cycleStartDate: '',
      effectiveFrom: '',
      effectiveTo: '',
      toleranceRules: {
        entryToleranceMinutes: '',
        earlyExitToleranceMinutes: '',
        roundingMarginMinutes: '',
      },
      overtimePolicy: {
        ignoreOvertime: false,
      },
      turnPolicy: {
        enableAutoClose: false,
        autoCloseGraceMinutes: '',
      },
    };
  }

  private mapWorkAssignmentToForm(workAssignment: OrgUnitWorkAssignment | null | undefined): WorkAssignmentForm {
    if (!workAssignment) {
      return this.createEmptyWorkAssignmentForm();
    }

    return {
      enabled: true,
      workCycleId: workAssignment.workCycleId || '',
      restConfigurationId: workAssignment.restConfigurationId || '',
      cycleStartDate: workAssignment.cycleStartDate || '',
      effectiveFrom: workAssignment.effectiveFrom || workAssignment.cycleStartDate || '',
      effectiveTo: workAssignment.effectiveTo || '',
      toleranceRules: {
        entryToleranceMinutes: workAssignment.toleranceRules?.entryToleranceMinutes ?? '',
        earlyExitToleranceMinutes: workAssignment.toleranceRules?.earlyExitToleranceMinutes ?? '',
        roundingMarginMinutes: workAssignment.toleranceRules?.roundingMarginMinutes ?? '',
      },
      overtimePolicy: {
        ignoreOvertime: !!workAssignment.overtimePolicy?.ignoreOvertime,
      },
      turnPolicy: {
        enableAutoClose: !!workAssignment.turnPolicy?.enableAutoClose,
        autoCloseGraceMinutes: workAssignment.turnPolicy?.autoCloseGraceMinutes ?? '',
      },
    };
  }

  private buildWorkAssignmentPayload():
    | {
        orgUnitId?: string;
        workCycleId: string;
        restConfigurationId?: string;
        cycleStartDate: string;
        effectiveFrom?: string;
        effectiveTo: string | null;
        toleranceRules?: WorkAssignmentToleranceRules | null;
        overtimePolicy?: WorkAssignmentOvertimePolicy | null;
        turnPolicy?: WorkAssignmentTurnPolicy | null;
      }
    | null {
    if (!this.workAssignmentForm.enabled) {
      return null;
    }

    if (!this.workAssignmentForm.workCycleId) {
      this.showNotification(
        'snackbar-warning',
        this.t('orgUnitsCatalog.notifications.validationWorkCycle'),
        'bottom',
        'center'
      );
      return null;
    }

    if (!this.workAssignmentForm.cycleStartDate) {
      this.showNotification(
        'snackbar-warning',
        this.t('workAssignmentRules.validationCycleStartDate'),
        'bottom',
        'center'
      );
      return null;
    }

    if (!this.hasValidWorkAssignmentDateRange()) {
      this.showNotification(
        'snackbar-warning',
        this.t('workAssignmentRules.validationDateRange'),
        'bottom',
        'center'
      );
      return null;
    }

    return {
      workCycleId: this.workAssignmentForm.workCycleId,
      ...(this.workAssignmentForm.restConfigurationId
        ? { restConfigurationId: this.workAssignmentForm.restConfigurationId }
        : {}),
      cycleStartDate: this.workAssignmentForm.cycleStartDate,
      // cycleOffset is intentionally omitted until offset-based assignments are enabled again.
      ...(this.shouldSendEffectiveFrom() ? { effectiveFrom: this.workAssignmentForm.effectiveFrom } : {}),
      effectiveTo: this.workAssignmentForm.effectiveTo || null,
      toleranceRules: {
        entryToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.entryToleranceMinutes),
        earlyExitToleranceMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.earlyExitToleranceMinutes),
        roundingMarginMinutes: this.toNullableNumber(this.workAssignmentForm.toleranceRules.roundingMarginMinutes),
      },
      overtimePolicy: {
        ignoreOvertime: this.workAssignmentForm.overtimePolicy.ignoreOvertime,
      },
      turnPolicy: {
        enableAutoClose: this.workAssignmentForm.turnPolicy.enableAutoClose,
        ...(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes !== ''
          ? { autoCloseGraceMinutes: Number(this.workAssignmentForm.turnPolicy.autoCloseGraceMinutes) }
          : {}),
      },
    };
  }

  private validateAttendanceConfigForm(): string | null {
    const validationKey = validateAttendanceConfigForm(this.attendanceConfigForm);
    return validationKey ? this.t(validationKey) : null;
  }

  private async persistAttendanceConfig(): Promise<void> {
    if (!this.selectedOrgUnitId || !this.canManageAttendanceConfig) {
      return;
    }

    if (!this.attendanceConfigForm.enabled) {
      if (this.selectedAttendanceConfig?.attendanceConfigId) {
        await firstValueFrom(this.apiService.delete(`attendance/configs/${this.selectedAttendanceConfig.attendanceConfigId}`));
      }
      this.selectedAttendanceConfig = null;
      this.attendanceConfigForm = createEmptyAttendanceConfigForm();
      return;
    }

    const payload = buildAttendanceConfigPayload(this.attendanceConfigForm, {
      orgUnitId: this.selectedOrgUnitId,
    });

    if (!payload) {
      return;
    }

    if (this.selectedAttendanceConfig?.attendanceConfigId) {
      this.selectedAttendanceConfig = await firstValueFrom(
        this.apiService.patch<AttendanceConfigRecord>(
          `attendance/configs/${this.selectedAttendanceConfig.attendanceConfigId}`,
          payload
        )
      );
      return;
    }

    this.selectedAttendanceConfig = await firstValueFrom(
      this.apiService.post<AttendanceConfigRecord>('attendance/configs', payload)
    );
  }

  private resetUsersState(): void {
    this.allowUsers = [];
    this.denyUsers = [];
    this.availableUsers = [];
    this.selectedAllowUserId = '';
    this.selectedDenyUserId = '';
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'orgUnits', action });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private toNullableNumber(value: number | '' | null | undefined): number | null {
    return value === '' || value === null || value === undefined ? null : Number(value);
  }

  private getCompanySearchText(value: string | CompanyRecord | null | undefined): string {
    if (!value) {
      return '';
    }

    return typeof value === 'string' ? value : value.name || '';
  }

  private buildAssignmentScheduleCalendarMonth(
    monthDate: Date,
    start: Date,
    end: Date | null,
    cycleDays: WorkCyclePreviewDay[]
  ): AssignmentScheduleCalendarMonth {
    const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const gridStart = this.startOfWeek(monthStart);
    const gridEnd = this.endOfWeek(monthEnd);
    const today = this.parseLocalDate(this.toIsoDateString(new Date()));
    const weekdayFormatter = new Intl.DateTimeFormat(this.getCalendarLocale(), { weekday: 'long' });
    const days: AssignmentScheduleCalendarDay[] = [];

    for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor = this.addDays(cursor, 1)) {
      const isInRange = cursor >= start && (!end || cursor <= end);
      const weekdayLabel = weekdayFormatter.format(cursor);
      days.push({
        date: new Date(cursor),
        dayNumber: cursor.getDate(),
        isCurrentMonth: cursor.getMonth() === monthStart.getMonth(),
        isStart: this.isSameDate(cursor, start),
        isEnd: !!end && this.isSameDate(cursor, end),
        isInRange,
        isToday: !!today && this.isSameDate(cursor, today),
        previewDay: isInRange ? this.getAppliedCycleDayForDate(cursor, cycleDays) : null,
        weekdayLabel: weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1),
      });
    }

    return {
      label: new Intl.DateTimeFormat(this.getCalendarLocale(), { month: 'long', year: 'numeric' }).format(monthStart),
      days,
    };
  }

  private parseLocalDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private startOfWeek(value: Date): Date {
    const normalized = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const day = normalized.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    normalized.setDate(normalized.getDate() + mondayOffset);
    return normalized;
  }

  private endOfWeek(value: Date): Date {
    return this.addDays(this.startOfWeek(value), 6);
  }

  private addDays(value: Date, days: number): Date {
    const next = new Date(value.getFullYear(), value.getMonth(), value.getDate());
    next.setDate(next.getDate() + days);
    return next;
  }

  private isSameDate(left: Date, right: Date): boolean {
    return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
  }

  private getCalendarLocale(): string {
    return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
  }

  private getDailyScheduleRangeLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours');
    }

    if (schedule.mode === 'entry_window_duration') {
      if (schedule.entryWindowStartTime && schedule.entryWindowEndTime) {
        return `${schedule.entryWindowStartTime} - ${schedule.entryWindowEndTime}`;
      }
      return this.t('orgUnitsCatalog.workAssignment.cyclePreview.flexibleWindow');
    }

    if (schedule.mode === 'split_shift') {
      if (schedule.blocks?.length) {
        return schedule.blocks.map((block) => `${block.startTime} - ${block.endTime}`).join(' · ');
      }
      return this.t('orgUnitsCatalog.workAssignment.cyclePreview.splitShift');
    }

    if (schedule.startTime && schedule.endTime) {
      return `${schedule.startTime} - ${schedule.endTime}`;
    }

    return this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours');
  }

  private getDailyScheduleHoursLabel(schedule: DailyScheduleConfig | null): string {
    if (!schedule) {
      return this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours');
    }

    const totalMinutes = this.getDailyScheduleIntervals(schedule).reduce(
      (sum, interval) => sum + Math.max(0, interval.end - interval.start),
      0
    );
    if (totalMinutes <= 0) {
      return this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours');
    }

    const hours = totalMinutes / 60;
    return Number.isInteger(hours)
      ? this.t('orgUnitsCatalog.workAssignment.cyclePreview.hoursWhole', { hours })
      : this.t('orgUnitsCatalog.workAssignment.cyclePreview.hoursFraction', { hours: hours.toFixed(1) });
  }

  private getDailyScheduleIntervals(schedule: DailyScheduleConfig): Array<{ start: number; end: number }> {
    if (schedule.blocks?.length) {
      return schedule.blocks
        .map((block) => this.createMinuteInterval(block.startTime, block.endTime))
        .filter((interval): interval is { start: number; end: number } => !!interval);
    }

    const interval =
      schedule.mode === 'entry_window_duration'
        ? this.createMinuteInterval(schedule.entryWindowStartTime, schedule.entryWindowEndTime)
        : this.createMinuteInterval(schedule.startTime, schedule.endTime);
    return interval ? [interval] : [];
  }

  private createMinuteInterval(startTime?: string | null, endTime?: string | null): { start: number; end: number } | null {
    const start = this.normalizeMinuteValue(startTime);
    const end = this.normalizeMinuteValue(endTime);

    if (start === null || end === null) {
      return null;
    }

    return end > start ? { start, end } : { start, end: end + 1440 };
  }

  private normalizeMinuteValue(value?: string | null): number | null {
    if (!value || !value.includes(':')) {
      return null;
    }

    const [hours, minutes] = value.split(':').map(Number);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }

    return hours * 60 + minutes;
  }

  private getAssignmentCyclePreviewReferenceDate(): Date | null {
    return this.parseLocalDate(this.workAssignmentForm.effectiveFrom) || this.parseLocalDate(this.workAssignmentForm.cycleStartDate) || null;
  }

  private getAppliedCycleDayForDate(date: Date, cycleDays: WorkCyclePreviewDay[]): WorkCyclePreviewDay {
    const cycleLength = cycleDays.length;
    const cycleStartDate = this.parseLocalDate(this.workAssignmentForm.cycleStartDate) || this.getAssignmentCyclePreviewReferenceDate() || date;
    const daysDiff = Math.floor((date.getTime() - cycleStartDate.getTime()) / 86400000);
    const normalizedIndex = this.mod(daysDiff, cycleLength);
    const cycleDay = cycleDays[normalizedIndex] || cycleDays[0];

    return this.isRestConfiguredForDate(date, cycleDay)
      ? this.toConfiguredRestPreviewDay(cycleDay)
      : cycleDay;
  }

  private isRestConfiguredForDate(date: Date, cycleDay: WorkCyclePreviewDay): boolean {
    if (cycleDay.isRest) {
      return true;
    }

    const restConfiguration = this.getSelectedRestConfiguration();
    if (!restConfiguration || restConfiguration.isActive === false) {
      return false;
    }

    const mode = String(restConfiguration.mode || '').trim().toLowerCase();
    if (mode === 'fixed_weekday') {
      const weekday = date.getDay() === 0 ? 7 : date.getDay();
      return (restConfiguration.weekdays || []).map(Number).includes(weekday);
    }

    if (mode === 'cycle_reference') {
      if (
        restConfiguration.cycleReference &&
        restConfiguration.cycleReference !== this.workAssignmentForm.workCycleId
      ) {
        return false;
      }

      return (restConfiguration.restDays || []).map(Number).includes(cycleDay.dayOrder);
    }

    return false;
  }

  private getSelectedRestConfiguration(): RestConfigurationConfig | null {
    const restConfigurationId = this.workAssignmentForm.restConfigurationId;
    if (!restConfigurationId) {
      return null;
    }

    return this.restConfigurations.find(
      (item) => item.restConfigurationId === restConfigurationId
    ) || null;
  }

  private toConfiguredRestPreviewDay(cycleDay: WorkCyclePreviewDay): WorkCyclePreviewDay {
    return {
      ...cycleDay,
      dayType: 'rest',
      scheduleName: this.t('orgUnitsCatalog.workAssignment.cyclePreview.rest'),
      scheduleRange: this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours'),
      expectedHours: this.t('orgUnitsCatalog.workAssignment.cyclePreview.noHours'),
      isRest: true,
    };
  }

  private updateWorkAssignmentDate(
    target: 'cycleStartDate' | 'effectiveFrom' | 'effectiveTo',
    value: string
  ): void {
    const previousCycleStart = this.workAssignmentForm.cycleStartDate;
    this.workAssignmentForm[target] = value;
    if (
      target === 'cycleStartDate' &&
      (!this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.effectiveFrom === previousCycleStart)
    ) {
      this.workAssignmentForm.effectiveFrom = value;
    }
    this.onWorkAssignmentDatesChange();
  }

  private hasValidWorkAssignmentDateRange(): boolean {
    const start = this.parseLocalDate(
      this.workAssignmentForm.effectiveFrom || this.workAssignmentForm.cycleStartDate
    );
    const end = this.parseLocalDate(this.workAssignmentForm.effectiveTo);
    return !start || !end || end.getTime() >= start.getTime();
  }

  private shouldSendEffectiveFrom(): boolean {
    return !!this.workAssignmentForm.effectiveFrom &&
      this.workAssignmentForm.effectiveFrom !== this.workAssignmentForm.cycleStartDate;
  }

  private mod(value: number, base: number): number {
    if (!base) {
      return 0;
    }
    return ((value % base) + base) % base;
  }

  private toIsoDateString(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }
}

